import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent,
} from "react";
import ChevronDownIcon from "@/assets/icons/common/ChevronDownIcon";
import { openAfterKeyboardDismiss } from "@/shared/lib/openAfterKeyboardDismiss";
import { formatPhoneProgressive } from "@/shared/lib/format";
import { useTapInteraction } from "@/shared/ui/pressable";
import InputShell from "./InputShell";
import styles from "./PhoneInput.module.scss";

type PhoneInputProps = {
  label?: string;
  /** 통신사 버튼에 표시할 텍스트. 미선택 시 placeholder 문구를 그대로 넘긴다. */
  carrierLabel: string;
  /** carrierLabel 이 placeholder 인 경우 true (회색 처리). */
  carrierPlaceholder?: boolean;
  value: string;
  placeholder?: string;
  maxLength?: number;
  onCarrierClick: () => void;
  onChange: (value: string) => void;
  disabled?: boolean;
  tabIndex?: number;
  className?: string;
};

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  function PhoneInput(
    {
      label = "휴대폰번호",
      carrierLabel,
      carrierPlaceholder = false,
      value,
      placeholder = "휴대폰번호 입력",
      maxLength = 11,
      onCarrierClick,
      onChange,
      disabled,
      tabIndex,
      className,
    },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const { isPressed: carrierPressed, ref: carrierRef, pressHandlers: carrierHandlers } = useTapInteraction<HTMLButtonElement>({ disabled });
    const [focused, setFocused] = useState(false);
    // 통신사가 선택돼 있거나 휴대폰번호가 입력돼 있으면 filled 로 본다.
    const filled = value.length > 0 || !carrierPlaceholder;
    // empty + blur 상태에서는 다른 input 과 동일하게 label 만 표시.
    // 통신사 버튼은 focus 또는 값이 채워졌을 때만 노출.
    const isCompact = focused || filled;

    // 표시용 하이픈 값(010-2316-2110). 데이터(value)는 digits 원본이라 onChange/검증 계약 불변.
    const displayValue = formatPhoneProgressive(value);
    // DOM maxLength 는 하이픈 포함 표시 길이 — 11 digits → "010-2316-2110"(13). 하이픈 자리를 확보한다.
    const displayMaxLength = formatPhoneProgressive("0".repeat(maxLength)).length;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value.replace(/[^0-9]/g, "").slice(0, maxLength);
      onChange(next);
    };

    const handleCarrierBtnClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      // 휴대폰번호 input 포커스가 남아 있으면 native keyboard 가 다 내려간 뒤 carrier sheet 를
      // 연다(같은 tick 오픈 시 키보드와 겹침 — openAfterKeyboardDismiss 가 blur+대기 처리).
      openAfterKeyboardDismiss(onCarrierClick);
    };

    // mousedown 단계에서 default focus 이동을 막아 input focus 가 남아 있어도
    // click event 가 keyboard 닫힘 reflow 와 충돌해 누락되지 않도록 한다.
    const handleCarrierBtnMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
    };

    return (
      <InputShell
        label={label}
        focused={focused}
        filled={filled}
        disabled={disabled}
        className={className}
        onClick={() => inputRef.current?.focus()}
      >
        <div className={styles.row}>
          {isCompact && (
            <button
              ref={carrierRef}
              type="button"
              className={`${styles.carrierBtn} ${carrierPressed ? styles.pressed : ""}`.trim()}
              onMouseDown={handleCarrierBtnMouseDown}
              onClick={handleCarrierBtnClick}
              aria-label="통신사 선택"
              tabIndex={tabIndex}
              disabled={disabled}
              {...carrierHandlers}
            >
              <span
                className={[
                  styles.carrierText,
                  carrierPlaceholder ? styles.carrierPlaceholder : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {carrierLabel}
              </span>
              <ChevronDownIcon width="24" height="24" />
            </button>
          )}
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            inputMode="numeric"
            // value 에 하이픈이 포함되므로 [0-9]* 는 :invalid 오판 소지 — 하이픈을 허용한다.
            pattern="[0-9-]*"
            maxLength={displayMaxLength}
            placeholder={focused ? placeholder : ""}
            value={displayValue}
            onChange={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-label="휴대폰번호"
            tabIndex={tabIndex}
            disabled={disabled}
            autoComplete="tel-national"
          />
        </div>
      </InputShell>
    );
  },
);

export default PhoneInput;
export type { PhoneInputProps };
